import en from "./locales/en.json";
import es from "./locales/es.json";
import fr from "./locales/fr.json";
import ar from "./locales/ar.json";
import pt from "./locales/pt.json";
import hi from "./locales/hi.json";
import sw from "./locales/sw.json";
import zh from "./locales/zh.json";
import yo from "./locales/yo.json";
import ig from "./locales/ig.json";
import ha from "./locales/ha.json";

export type Language = "en" | "es" | "fr" | "zh" | "ar" | "pt" | "hi" | "sw" | "yo" | "ig" | "ha";

export interface Translation {
  [key: string]: any;
}

const translations: Record<Language, Translation> = {
  en,
  es,
  fr,
  zh,
  ar,
  pt,
  hi,
  sw,
  yo,
  ig,
  ha
};

export const SUPPORTED_LANGUAGES: Language[] = ["en", "es", "fr", "zh", "ar", "pt", "hi", "sw", "yo", "ig", "ha"];

export const LANGUAGE_LABELS: Record<Language, string> = {
  en: "English",
  es: "Español",
  fr: "Français",
  zh: "中文",
  ar: "العربية",
  pt: "Português",
  hi: "हिन्दी",
  sw: "Kiswahili",
  yo: "Yorùbá",
  ig: "Igbo",
  ha: "Hausa"
};

/**
 * Get translation value from nested object using dot notation
 * @param obj - Translation object
 * @param path - Dot-separated path (e.g., "common.save")
 * @param defaultValue - Fallback value if key not found
 */
export const getNestedTranslation = (
  obj: Translation,
  path: string,
  defaultValue: string = path
): string => {
  const keys = path.split(".");
  let value: any = obj;

  for (const key of keys) {
    value = value?.[key];
  }

  return value || defaultValue;
};

/**
 * Get all translations for a specific language
 */
export const getLanguageTranslations = (language: Language): Translation => {
  return translations[language] || translations.en;
};

/**
 * Get single translation by key and language
 */
export const translate = (
  key: string,
  language: Language,
  defaultValue?: string
): string => {
  const translationObj = getLanguageTranslations(language);
  return getNestedTranslation(translationObj, key, defaultValue || key);
};

/**
 * Batch translate multiple keys
 */
export const translateMultiple = (
  keys: string[],
  language: Language
): Record<string, string> => {
  const result: Record<string, string> = {};
  keys.forEach((key) => {
    result[key] = translate(key, language);
  });
  return result;
};

/**
 * Check if language is supported
 */
export const isLanguageSupported = (language: string): language is Language => {
  return SUPPORTED_LANGUAGES.includes(language as Language);
};

/**
 * Get browser language or fallback to default
 */
export const getBrowserLanguage = (): Language => {
  const browserLang = navigator.language.split("-")[0];
  if (isLanguageSupported(browserLang)) {
    return browserLang;
  }
  return "en";
};
